export type AuthUser = {
  id: string;
  email: string | null;
  user_metadata?: Record<string, unknown>;
  app_metadata?: Record<string, unknown>;
};

type ApiOptions = RequestInit & {
  skipUnauthorizedThrow?: boolean;
};

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

const parseJson = async (response: Response) => {
  const text = await response.text();
  if (!text) {
    return {} as Record<string, unknown>;
  }

  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return { error: 'Invalid server response' };
  }
};

const request = async <T>(path: string, options?: ApiOptions): Promise<T> => {
  const hasBody = options?.body !== undefined && options?.body !== null;
  const baseHeaders: HeadersInit = hasBody ? { 'Content-Type': 'application/json' } : {};

  const response = await fetch(`/functions/${path}`, {
    credentials: 'include',
    headers: {
      ...baseHeaders,
      ...(options?.headers || {})
    },
    ...options
  });

  const payload = await parseJson(response);

  if (!response.ok) {
    const message =
      typeof payload.error === 'string'
        ? payload.error
        : `Request failed (${response.status})`;

    if (options?.skipUnauthorizedThrow && response.status === 401) {
      throw new ApiError(message, response.status);
    }

    throw new ApiError(message, response.status);
  }

  return payload as T;
};

export const backendApi = {
  login: (email: string, password: string) =>
    request<{ user: AuthUser; isAdmin: boolean }>('login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    }),

  logout: () =>
    request<{ success: boolean }>('logout', {
      method: 'POST'
    }),

  sessionContext: () =>
    request<{ user: AuthUser | null; isAdmin: boolean }>('session-context', {
      method: 'GET'
    }),

  signUp: (email: string, password: string, fullName?: string, redirectTo?: string) =>
    request<{ user?: AuthUser; isAdmin?: boolean; emailConfirmationRequired?: boolean }>('signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, fullName, redirectTo })
    }),

  sendPasswordReset: (email: string, redirectTo?: string) =>
    request<{ success: boolean }>('send-password-reset', {
      method: 'POST',
      body: JSON.stringify({ email, redirectTo })
    }),

  setRecoverySession: (accessToken: string, refreshToken: string) =>
    request<{ success: boolean }>('recovery-session', {
      method: 'POST',
      body: JSON.stringify({ accessToken, refreshToken })
    }),

  resendConfirmation: (email: string, redirectTo?: string) =>
    request<{ success: boolean }>('resend-confirmation', {
      method: 'POST',
      body: JSON.stringify({ email, redirectTo })
    }),

  verifyEmailOtp: (email: string, token: string) =>
    request<{ success: boolean; emailConfirmed?: boolean; user?: AuthUser; isAdmin?: boolean }>('verify-email-otp', {
      method: 'POST',
      body: JSON.stringify({ email, token })
    }),

  updatePassword: (password: string) =>
    request<{ success: boolean }>('update-password', {
      method: 'POST',
      body: JSON.stringify({ password })
    }),

  deleteAccount: () =>
    request<{ success: boolean }>('delete-user', {
      method: 'POST'
    }),

  startGoogleLogin: (next: string) =>
    request<{ url: string }>('google-login', {
      method: 'POST',
      body: JSON.stringify({ next })
    }),

  listInstagramAnalyses: () =>
    request<{ analyses: unknown[] }>('instagram-analyses', {
      method: 'GET'
    }),

  saveInstagramAnalysis: (payload: Record<string, unknown>) =>
    request<{ analysis: unknown }>('instagram-analyses', {
      method: 'POST',
      body: JSON.stringify(payload)
    }),

  deleteInstagramAnalysis: (analysisId: string) =>
    request<{ success: boolean }>('instagram-analyses', {
      method: 'DELETE',
      body: JSON.stringify({ analysisId })
    }),

  startUserSession: (payload: Record<string, unknown>) =>
    request<{ id: string; started_at: string }>('user-sessions', {
      method: 'POST',
      body: JSON.stringify(payload)
    }),

  heartbeatUserSession: (sessionId: string) =>
    request<{ success: boolean }>('user-sessions', {
      method: 'PATCH',
      body: JSON.stringify({ action: 'heartbeat', sessionId })
    }),

  endUserSession: (sessionId: string, durationSeconds: number) =>
    request<{ success: boolean }>('user-sessions', {
      method: 'PATCH',
      body: JSON.stringify({ action: 'end', sessionId, durationSeconds })
    }),

  submitFeedback: (payload: {
    category: 'issue' | 'idea' | 'other';
    rating: number;
    message: string;
    contactEmail?: string | null;
    metadata?: Record<string, unknown>;
  }) =>
    request<{ success: boolean }>('user-feedback', {
      method: 'POST',
      body: JSON.stringify(payload)
    }),

  getAdminDashboardData: () =>
    request<{
      sessions: unknown[];
      allSessions: unknown[];
      activeSessionsRaw?: unknown[];
      dailyRollups?: unknown[];
      loginEvents: unknown[];
      allProfiles: unknown[];
      recentProfiles: unknown[];
      overview: unknown[];
      analyses: unknown[];
      feedbackRows: unknown[];
      authUsers: unknown[];
      profileCount: number;
    }>('admin-dashboard-data', {
      method: 'GET'
    })
};
